import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { TaskModalSeed } from '@app/components/Modals/TaskModal';
import type { NTaskListProvider } from '@app/providers/TaskListProvider/types';
import type { NCrmTasks } from '@app/services/rtkQuery/CrmTasks/types';

import type { BaseOptionType } from 'antd/es/select';

interface UseTaskModalArgs {
  selectTaskForAction(target: number | NCrmTasks.ModelCrmTask): void;
  // Calendar "responsible" filter; a single pick becomes the new task's executor.
  selectedEmployee: BaseOptionType[];
}

/**
 * Drives the single unified TaskModal. It re-implements the legacy
 * preview/create/edit open handlers with the SAME signatures so every existing
 * call site keeps working while all of them now open one modal.
 * `taskId === null` → create mode; a number → view/edit.
 */
export const useTaskModal = ({
  selectTaskForAction,
  selectedEmployee,
}: UseTaskModalArgs) => {
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') as NTaskListProvider.Tab | null;
  const currentDate: NTaskListProvider.CurrentDate = useMemo(
    () => ({
      year: +(searchParams.get('year') || ''),
      month: +(searchParams.get('month') || ''),
      day: +(searchParams.get('day') || ''),
      week: +(searchParams.get('week') || ''),
    }),
    [searchParams]
  );

  // Only the calendar tabs show the responsible filter in the page header.
  // "Без ответственного" (value 0) and deleted users can't be assigned.
  const filteredExecutor = useMemo<TaskModalSeed['executor']>(() => {
    if (activeTab === 'registry' || selectedEmployee.length !== 1) {
      return undefined;
    }
    const [employee] = selectedEmployee;
    if (!employee.value || employee.IsDeleted) return undefined;

    return {
      id: Number(employee.value),
      name: employee.filterName || String(employee.value),
    };
  }, [activeTab, selectedEmployee]);

  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [taskModalId, setTaskModalId] = useState<number | null>(null);
  const [taskModalSeed, setTaskModalSeed] = useState<TaskModalSeed | undefined>(
    undefined
  );

  const openTaskModal = useCallback(
    (id: number | null, seed?: TaskModalSeed) => {
      setTaskModalId(id);
      setTaskModalSeed(seed);
      setIsTaskModalOpen(true);
    },
    []
  );

  const closeTaskModal = useCallback(() => {
    setIsTaskModalOpen(false);
  }, []);

  const openPreviewTaskModal = useCallback(
    (target: number | NCrmTasks.ModelCrmTask) => {
      selectTaskForAction(target);
      openTaskModal(typeof target === 'number' ? target : target.Id);
    },
    [openTaskModal, selectTaskForAction]
  );

  const openEditTaskModal = useCallback(
    (task: NCrmTasks.ModelCrmTask) => {
      selectTaskForAction(task);
      openTaskModal(task.Id);
    },
    [openTaskModal, selectTaskForAction]
  );

  const openCreateTaskModal = useCallback(() => {
    openTaskModal(null, { executor: filteredExecutor });
  }, [filteredExecutor, openTaskModal]);

  const createTaskForRange: NTaskListProvider.State['createTaskForRange'] =
    useCallback(
      ([start, end], taskDay) => {
        const { year, month, day } = taskDay || currentDate;
        openTaskModal(null, {
          timeStart: `${year}-${month}-${day}T${start}:00`,
          timeDeadline: `${year}-${month}-${day}T${end}:00`,
          executor: filteredExecutor,
        });
      },
      [currentDate, filteredExecutor, openTaskModal]
    );

  const createTaskForLead: NTaskListProvider.State['createTaskForLead'] =
    useCallback(
      ({ ClientId }) => {
        openTaskModal(null, { clientId: ClientId ?? null });
      },
      [openTaskModal]
    );

  return {
    isTaskModalOpen,
    taskModalId,
    taskModalSeed,
    openTaskModal,
    closeTaskModal,
    openPreviewTaskModal,
    openEditTaskModal,
    openCreateTaskModal,
    createTaskForRange,
    createTaskForLead,
  };
};
